import React from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  CardActions, 
  Typography, 
  Chip, 
  Box, 
  Button,
  Avatar,
  CardMedia,
  Fade
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonIcon from '@mui/icons-material/Person';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { Idea} from '../types';
import { formatDate } from '../utils/formatDate';

interface IdeaCardProps {
  idea: Idea;
  onUpvote: (ideaId: string) => void;
  index?: number; // Optional index for staggered animations
}

const IdeaCard: React.FC<IdeaCardProps> = ({ idea, onUpvote, index = 0 }) => {
  const handleUpvote = (event: React.MouseEvent) => {
    event.preventDefault(); // Prevent navigation when clicking the upvote button
    onUpvote(idea.id);
  };

  // Get first letter of each tag to create a colorful avatar
  const getAvatarInitial = () => {
    if (idea.is_anonymous) {
      return 'A';
    }
    if (idea.user_display_name) {
      return idea.user_display_name[0].toUpperCase();
    }
    return 'I';
  };

  // Get a deterministic color based on the author's name
  const getAvatarColor = () => {
    // Use grey for anonymous posts
    if (idea.is_anonymous) return '#9e9e9e';
    if (!idea.user_display_name) return '#3c5770';
    
    const colors = [
      '#e57373', '#f06292', '#ba68c8', '#9575cd', 
      '#7986cb', '#64b5f6', '#4fc3f7', '#4dd0e1', 
      '#4db6ac', '#81c784', '#aed581', '#dce775',
      '#fff176', '#ffd54f', '#ffb74d', '#ff8a65'
    ];
    
    // Simple hash function to choose a color based on the author
    let hash = 0;
    for (let i = 0; i < idea.user_display_name.length; i++) {
      hash = ((hash << 5) - hash) + idea.user_display_name.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    
    // Get a positive index within the colors array
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };
  
  // Get display name, showing "Anonymous" for anonymous posts
  const getDisplayName = () => {
    if (idea.is_anonymous) {
      return 'Anonymous';
    }
    return idea.user_display_name || 'Unknown User';
  };

  // Check if idea is liked by the user using either property
  const isLikedByUser = idea.liked_by_user || idea.userUpvoted;

  // Generate placeholder gradient based on idea id
  const getPlaceholderGradient = () => {
    if (!idea.id) return 'linear-gradient(135deg, #1a237e 30%, #303f9f 90%)';
    
    const colors = [
      ['#ff9a9e', '#fad0c4'],
      ['#a18cd1', '#fbc2eb'],
      ['#ffecd2', '#fcb69f'],
      ['#ff9a9e', '#fecfef'],
      ['#f6d365', '#fda085'],
      ['#84fab0', '#8fd3f4'],
      ['#a6c0fe', '#f68084'],
      ['#fccb90', '#d57eeb'],
      ['#e0c3fc', '#8ec5fc'],
      ['#f093fb', '#f5576c']
    ];
    
    // Use idea id to deterministically choose a gradient
    let hashValue = 0;
    for (let i = 0; i < idea.id.length; i++) {
      hashValue = idea.id.charCodeAt(i) + ((hashValue << 5) - hashValue);
    }
    
    const colorIndex = Math.abs(hashValue) % colors.length;
    return `linear-gradient(135deg, ${colors[colorIndex][0]} 0%, ${colors[colorIndex][1]} 100%)`;
  };

  // Staggered animation delay based on index
  const getAnimationDelay = () => {
    return Math.min(index * 100, 600); // Cap at 600ms for the latest cards
  };

  return (
    <Fade in={true} style={{ transitionDelay: `${getAnimationDelay()}ms` }} timeout={800}>
      <Card 
        component={RouterLink} 
        to={`/idea/${idea.id}`}
        sx={{ 
          textDecoration: 'none', 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
          backgroundColor: isLikedByUser ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)',
            '&::after': {
              transform: 'translateY(0)',
            },
            '.idea-card-image': {
              transform: 'scale(1.05)',
            }
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: (theme) => 
              `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            transform: 'translateY(-100%)',
            transition: 'transform 0.3s ease',
          }
        }}
      >
        {/* Display image if available, otherwise show placeholder */}
        <Box sx={{ 
          position: 'relative', 
          height: '160px', 
          overflow: 'hidden',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          {idea.image_url ? (
            <CardMedia
              component="img"
              height="160"
              image={idea.image_url}
              alt={idea.title}
              className="idea-card-image"
              sx={{ 
                objectFit: 'cover',
                transition: 'transform 0.5s ease',
              }}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                background: getPlaceholderGradient(),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.5s ease',
              }}
              className="idea-card-image"
            >
              <LightbulbOutlinedIcon 
                sx={{ 
                  fontSize: '3rem', 
                  color: 'white',
                  opacity: 0.9,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }} 
              />
            </Box>
          )}
        </Box>
        
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: getAvatarColor() }}>
              {getAvatarInitial()}
            </Avatar>
          }
          title={idea.title}
          subheader={
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <PersonIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.7, fontSize: '0.9rem' }} />
              <Typography variant="caption" sx={{ mr: 1, fontStyle: idea.is_anonymous ? 'italic' : 'normal' }}>
                {getDisplayName()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(idea.created_at)}
              </Typography>
            </Box>
          }
          sx={{
            '.MuiCardHeader-title': {
              fontWeight: 'bold',
              color: 'text.primary',
              fontSize: '1.125rem',
              lineHeight: 1.4,
              mb: 0.5,
            },
            '.MuiCardHeader-subheader': {
              color: 'text.secondary',
              fontSize: '0.875rem',
            },
          }}
        />
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {idea.description.length > 150 
              ? `${idea.description.substring(0, 150)}...` 
              : idea.description}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {idea.tags && idea.tags.map((tag) => (
              <Chip 
                key={tag.id} 
                label={tag.name} 
                size="small" 
                color="primary" 
                variant="outlined" 
                sx={{ 
                  mr: 0.5, 
                  mb: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    borderColor: 'primary.main',
                  }
                }}
              />
            ))}
          </Box>
        </CardContent>
        <CardActions 
          disableSpacing 
          sx={{ 
            borderTop: '1px solid',
            borderColor: 'divider',
            justifyContent: 'space-between',
            px: 2,
            py: 1
          }}
        >
          <Button 
            startIcon={isLikedByUser ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
            onClick={handleUpvote}
            color="primary"
            size="small"
            sx={{
              fontWeight: isLikedByUser ? 'bold' : 'normal',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'scale(1.05)',
                backgroundColor: 'transparent',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '2px',
                backgroundColor: 'primary.main',
                transform: 'scaleX(0)',
                transformOrigin: 'right',
                transition: 'transform 0.3s ease',
              },
              '&:hover::after': {
                transform: 'scaleX(1)',
                transformOrigin: 'left',
              },
              transition: 'transform 0.2s ease'
            }}
          >
            {idea.likes}
          </Button>
          <Button
            startIcon={<ChatBubbleOutlineIcon />}
            color="primary"
            size="small"
            sx={{
              position: 'relative',
              overflow: 'hidden',
              fontWeight: idea.comment_count > 0 ? 'bold' : 'normal',
              '&:hover': {
                transform: 'scale(1.05)',
                backgroundColor: 'transparent',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '2px',
                backgroundColor: 'primary.main',
                transform: 'scaleX(0)',
                transformOrigin: 'right',
                transition: 'transform 0.3s ease',
              },
              '&:hover::after': {
                transform: 'scaleX(1)',
                transformOrigin: 'left',
              },
              transition: 'transform 0.2s ease'
            }}
          >
            {idea.comment_count}
            <Box 
              component="span" 
              sx={{ 
                ml: 0.5, 
                fontSize: '0.75rem', 
                color: 'text.secondary',
                display: 'inline-block'
              }}
            >
              {idea.comment_count === 1 ? 'comment' : 'comments'}
            </Box>
            {idea.comment_count > 0 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  zIndex: 1,
                }}
              >
                {idea.comment_count}
              </Box>
            )}
          </Button>
        </CardActions>
      </Card>
    </Fade>
  );
};

export default IdeaCard; 